/**
 * Battle Cards — constants.
 *
 * Single source of truth for every card number. No magic numbers anywhere else.
 *
 * Design: requirements/BATTLE_CARDS_V4_LOADOUTS.md
 */

import {
  EVOLUTION_CARDS_PER_STEP,
  EVOLUTION_RANK_MAX,
  evolutionCardsForRank,
} from '../domain/battleEvolution.js';
import { ALL_COMBAT_ACTIONS, getGestureOutcome } from '../domain/combatActionRules.js';
import type { CardBattleSide, CardTier, CopyLadderRow } from '../domain/cardTypes.js';

/** Copies of every card in the pile. Also the meld ceiling — you cannot play a 5th. */
export const EFFECT_CARD_COPIES = 4;

/**
 * All three printings, weakest first — the order a tie is broken in.
 *
 * A LIST RATHER THAN A COMPARISON, and the third printing arriving is exactly what it was
 * shaped for: adding Epic touched this line and the two tables below it, and nothing in the
 * engine. {@link cardTierRank} is the only thing that knows how tiers order.
 *
 * **THE LADDER STOPS AT EPIC. There is no Legendary** — a fourth rung would need a copy
 * count above a full stack ({@link EFFECT_CARD_COPIES}), which is a card you can hold more
 * of than you can ever play.
 */
export const CARD_TIER_ORDER: readonly CardTier[] = ['common', 'rare', 'epic'];

/**
 * How a tier reads on a card face.
 *
 * The words come from `RARITY_NAMES`, deliberately, because a player already knows them —
 * but the TYPE does not (see {@link CardTier}). `cardCatalog.test.ts` asserts every value
 * here is a member of that pet-rarity union, which is the only link the two need.
 */
export const CARD_TIER_LABEL: Record<CardTier, string> = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
};

/**
 * **HOW MANY COPIES EACH TIER TAKES. The collection ladder, stated once.**
 *
 * A card is not bought at a tier — it is COLLECTED into one. **Every card starts as a Common
 * at ZERO copies**; two copies make it Rare, four make it Epic. Read as "this tier needs at
 * least N copies", walked weakest-last by {@link getCardTierForCopies}.
 *
 * **COMMON COSTS NOTHING, and that is the rung's whole job.** Every skill in the game is
 * equippable from day one at its Common printing, so a new player picks a loadout out of all
 * sixteen instead of out of the four they were handed; what the shop sells is the RUNG, not
 * the card. The alternative — a first copy that bought ACCESS — meant a shelf where three of
 * every four skills were a locked tile a player could not even try, and it also made the
 * first copy the only purchase that ever bought a player something other than a bigger
 * number.
 *
 * It is why `common` is 0 rather than 1, and that number is load-bearing twice over:
 * {@link copiesToNextTier} reads it to name the next rung, and at `common: 1` a card with no
 * copies reported "1 more for Common" — an upgrade from a printing to ITSELF, which the
 * Store's own `isAtEffectiveCeiling` correctly read as "nothing left to gain" and used to
 * close the sale on every uncollected card in the game.
 *
 * Why counts rather than printings on a shelf: a shop that sells you the Rare directly makes
 * the Common a thing you regret buying, and a player who cannot yet afford the Rare owns
 * nothing. Duplicates always mean something now, which is what makes a second drop of a card
 * you already have a good drop.
 *
 * Four is deliberately {@link EFFECT_CARD_COPIES} — a full stack, the most of one card a
 * hand can ever hold. The top of the collection ladder is the top of the play ladder.
 */
export const CARD_TIER_COPIES: Record<CardTier, number> = {
  common: 0,
  rare: 2,
  epic: EFFECT_CARD_COPIES,
};

/**
 * The tier a pet plays a card at, given how many copies it owns.
 *
 * Zero copies is `common`: every pet plays EVERY skill at the bottom rung without owning
 * anything, which is what makes the whole shelf equippable on day one. Anything above the
 * Epic bound stays Epic — there is no rung past it.
 */
export function getCardTierForCopies(copies: number): CardTier {
  let tier: CardTier = 'common';
  for (const candidate of CARD_TIER_ORDER) {
    if (copies >= CARD_TIER_COPIES[candidate]) tier = candidate;
  }
  return tier;
}

/**
 * Copies still needed to reach the next rung, or null when the card is already Epic.
 * The number the collection screen prints under a card ("1 more for Rare").
 */
export function copiesToNextTier(copies: number): { tier: CardTier; needed: number } | null {
  for (const candidate of CARD_TIER_ORDER) {
    const required = CARD_TIER_COPIES[candidate];
    if (copies < required) return { tier: candidate, needed: required - copies };
  }
  return null;
}

/** Where a tier sits on the ladder. Higher wins a mirror the copies could not settle. */
export function cardTierRank(tier: CardTier): number {
  return CARD_TIER_ORDER.indexOf(tier);
}

/**
 * **What a Common prints, as a share of its Rare twin. THE ONE DIAL.**
 *
 * Two invariants define the legal range, and both still hold at 0.75: a PAIR of Commons must
 * beat a lone Rare, so a wide shallow collection is a real strategy; and a full Common stack
 * must still lose to a full Rare stack, so the expensive card is still the better card. At
 * exactly 0.5 the first fails — two Commons equal one Rare and the tier decision collapses into
 * arithmetic.
 *
 * **0.85, MEASURED UP FROM 0.6, AND IT IS THE DIAL THREE CRITERIA SHARE.** At 0.6 the Common was
 * the starting loadout for every pet and every campaign opponent, and the run half read
 * **18.4% of campaigns clearing against a 90% floor** — the default printing was simply too
 * weak to finish the game with. The same dial sets criterion 1: at 0.6 a full Rare loadout beat
 * a full Common one **60.6% ±1.2%** of the time, just outside the 40-60% pay-to-win band, and
 * every step up closes that gap because it is the gap. Measured, on two seeds:
 *
 * | share | G1 campaigns clearing | 1 Rare vs Common | G2 retries removed |
 * |---|---|---|---|
 * | 0.60 | 18.4% | 60.6% ✗ | — |
 * | 0.70 | 71.1% | ~58% | −2.0% ✗ |
 * | 0.85 | **78.9%** | **55.7% ✓** | +2.6% ✗ |
 *
 * **G2 does not respond to this dial, and that is the finding.** It reads roughly zero at every
 * setting and at half the Rare price too, so there is no trade to make here: the dial is set for
 * G1 and criterion 1, which both move with it. Why buying cannot remove retries is a separate
 * problem, recorded in the tuning log.
 *
 * An attribution probe at 1.0 (Commons printed at full Rare value) recovered the clear rate only
 * to 55.3%, which is how the rest of the original drop was traced to the per-side piles rather
 * than to this number — see risk 1 in the requirement.
 */
export const COMMON_TIER_VALUE_SHARE = 0.85;

/**
 * **What an Epic prints, as a share of the Rare it grew out of.**
 *
 * The top rung has to be visibly worth collecting four copies for, and it must not be so far
 * ahead that a player who owns one is playing a different game. 1.15 puts an Epic almost
 * exactly as far above its Rare as the Rare sits above its Common (1 / 0.85 = 1.18), so the
 * ladder's three steps are even — the same reasoning that set the Common share, applied
 * upwards. Re-run the gauntlet before moving it: it is the same dial criterion 1 (does money
 * buy the game?) reads, from the other end.
 */
export const EPIC_TIER_VALUE_SHARE = 1.15;

/**
 * Every tier's multiplier on the card's base (Rare) number, in one table.
 *
 * `buildCard` reads this instead of branching on the tier, so adding a rung is a line here
 * and nothing in the catalogue. Rare is 1 by definition — it IS the base.
 */
export const CARD_TIER_VALUE_SHARE: Record<CardTier, number> = {
  common: COMMON_TIER_VALUE_SHARE,
  rare: 1,
  epic: EPIC_TIER_VALUE_SHARE,
};

/**
 * Where a full stack of Common binary cards lands.
 *
 * A binary card has no magnitude to scale, so the tier is stated as a CEILING instead and the
 * per-copy rate is derived from it. One line a player can hold: **four Rares is certainty;
 * four Commons is 75% and never gets there.**
 */
export const COMMON_BINARY_FULL_STACK_CHANCE = 0.75;

/**
 * How much a matched element is worth, as a multiplier on the card's value.
 *
 * A rider on a card you chose for other reasons, never the reason you chose it — which is what
 * the ceiling below enforces and what criterion 9's narrow band checks.
 *
 * **1.5, MEASURED UP FROM 1.25.** At 1.25 the whole mechanic was worth `+0.2 ±1.7` points of
 * win rate against a band that starts at +2 — flavour text, which is precisely the failure the
 * band's floor exists to catch. The scale to reason on comes from criterion 1: the Common-to-Rare
 * step is a 1.67x value jump and buys about three points per slot, so a rider has to be a real
 * fraction of that step to be noticed at all. 1.5 keeps it below the tier step, which is the
 * ordering the design wants — you upgrade the printing first and match the element second.
 */
export const CARD_MATCHED_ELEMENT_MULTIPLIER = 1.5;

/**
 * ...but never worth more HP per copy than the engine's own element advantage at the top of the
 * curve: a card rider that outpaid the mechanic it is named after would quietly replace it.
 *
 * It was derived from the old flat `ELEMENT_ADVANTAGE_BONUS` (15). Element advantage is now a share
 * of the caster's spell (`getElementAdvantageBonus`) — +17 on a median Lv20 spell — so it no longer
 * has one number to copy. 15 is kept on purpose: Row 74 moved the SKILL rider, not the card one,
 * and holding this still keeps the two changes separable when the gauntlet reads them.
 */
export const CARD_MATCHED_ELEMENT_MAX_BONUS = 15;

/**
 * Distinct cards ONE SIDE's pile holds — one per suit, because a loadout is one card per
 * gesture and a card may only sit in its own suit's slot.
 *
 * Derived from the combat wheel rather than typed as `4`, so the pile can never disagree with
 * the set of gestures the engine resolves.
 */
export const EFFECT_DECK_TYPES = ALL_COMBAT_ACTIONS.length;

/**
 * **HOW MANY SUIKAMON CARDS EACH SIDE'S PILE PRINTS — the mechanic's one dial.**
 *
 * A Suikamon card is not a move: it plays into no meld and adds no damage. What it does is
 * take a slot in a hand of {@link EFFECT_HAND_SIZE} and wait, and holding
 * `EVOLUTION_CARDS_PER_STEP` of them at once evolves the Suikamon on the spot. That is the
 * whole decision the card exists to create — **a hand one card weaker, now, for a Suikamon
 * that hits harder for the rest of the fight** — and this number decides how often a player
 * is asked it.
 *
 * **EVERY CARD THE LADDER COSTS, so the whole ladder is payable out of one pile.**
 *
 * It was four (matching {@link EFFECT_CARD_COPIES}) while the top rung cost six, which put the
 * last rung in exactly the failure mode `EVOLUTION_CARDS_PER_STEP` warns about: a rung nobody
 * stands on. Reaching it needed the pile to run out and the discard to shuffle back in, so
 * **Max Evo was a reshuffle away rather than a decision away** — and the cost of that was not
 * only the missing rung. The card AI holds a Suikamon card while a rung is in reach, so it was
 * holding cards toward a rung its own pile could not pay for; measured, the smart seat's edge
 * over a careless one fell to +16.1pts (`4`) and a good player's edge over a button-masher to
 * +15.7pts (`16`, floor +25). **A ladder whose top rung is unreachable is a ladder that stops
 * rewarding the decision it exists to create.**
 *
 * Derived from {@link evolutionCardsForRank} rather than authored, so the two can never drift:
 * whatever the ladder costs, a pile carries it.
 *
 * **It is still a balance dial and the gauntlet owns it** — more Suikamon cards means evolutions
 * come faster and hands are weaker while they are held; fewer and the mechanic stops appearing in
 * short fights. Row 44.
 */
export const SUIKAMON_CARDS_PER_PILE = evolutionCardsForRank(EVOLUTION_RANK_MAX);

/**
 * **The SKILL half of one side's pile**: every equipped card, {@link EFFECT_CARD_COPIES} times.
 *
 * **PER SIDE, NOT SHARED.** The old shared pile made the copy-odds table below an
 * approximation — two hands contending over one pile is not the model `countHandsByBestStack`
 * describes — and a loadout makes it exact: 4 types × 4 copies = 16.
 *
 * Split out from {@link EFFECT_DECK_TOTAL} when the Suikamon card landed, because the two
 * numbers answer different questions and only this one is about STACKS: the copy ladder is a
 * fact about how many copies of a skill a pile holds, while what a hand is drawn from is the
 * whole pile, Suikamon cards included.
 */
export const EFFECT_SKILL_CARDS_TOTAL = EFFECT_DECK_TYPES * EFFECT_CARD_COPIES;

/**
 * One side's whole pile — **the skills AND the Suikamon cards shuffled in with them.**
 *
 * The number every draw, every odds row and every "cards left" count is measured against. It
 * grew when the Suikamon card landed, and that is the point: a Suikamon card competes for the
 * same draw as a move, which is exactly what makes keeping one cost something.
 */
export const EFFECT_DECK_TOTAL = EFFECT_SKILL_CARDS_TOTAL + SUIKAMON_CARDS_PER_PILE;

/**
 * **WHAT AN EMPTY SLOT'S CARD IS WORTH: a small, flat chip per copy, paid in ITS OWN SUIT.**
 *
 * A base card used to be worth literally nothing — a slot with no collected skill dealt a card
 * that resolved to no outcome at all. It kept the hand the right size and did not one other
 * thing, which made the opening hours of the game a cards-off battle wearing a card layer: five
 * tiles on screen, none of them a decision.
 *
 * So the empty slot now lands. Three per copy, ×N up the same flat ladder as everything else, so
 * a full stack of four is 12 — **one point above the single-copy value of the deck's most
 * expensive Common** (Drain Life, 12) and a long way below what four copies of any real card
 * lands. That is the shape the number is chosen for: a base card is never the reason you win a
 * round, it is what a suit you have not collected yet still puts on the board.
 *
 * **THE MAGNITUDE IS ONE NUMBER; THE CURRENCY IS THE SUIT'S.** Attack, magic and dodge are paid
 * in damage and recover is paid in HP — `BASE_CARD_BY_GESTURE` sets each blank's `unit`, and the
 * engine pays whatever the face prints. It used to be damage on all four, and that was a bug a
 * player found the only way anyone could: they pressed Recover, nobody was bleeding, and the
 * opponent's bar went down. A move whose whole promise is "this is the one that does not hit
 * them" cannot hit them because of a slot they have not filled yet — and a blank has no business
 * inventing a currency its suit does not deal in. Three HP is still a chip on the board, so the
 * reason the number exists at all survives intact.
 *
 * It is authored rather than derived, and deliberately so. Every derivation available prices it
 * against a SKILL — a share of the weakest Common, a fraction of a plain attack — and a base
 * card is the absence of a skill, so any such formula would move this number every time an
 * unrelated card was re-priced. Three is chosen against the HP pool and pinned by a test.
 */
export const BASE_CARD_CHIP = 3;

/** Cards held at the start of a round. */
export const EFFECT_HAND_SIZE = 5;


/**
 * The two seats at a battle, as a LIST — so anything that has to walk both of them
 * (the engine's round loop, the table below) counts seats instead of typing `2`.
 */
export const CARD_BATTLE_SIDES: readonly CardBattleSide[] = ['challenger', 'defender'];

/**
 * **THE ROUND'S BOOST: how many of the four suits are boosted each round.**
 *
 * Two, so a round says something without saying everything — half the wheel is up, half is not,
 * and the choice between them is the decision the boost exists to create. At one it is a nudge
 * you often cannot use; at three there is almost always a boosted suit in your hand and the
 * roll stops meaning anything.
 */
export const ROUND_BOOST_SUITS = 2;

/**
 * **WHAT A BOOST IS WORTH, IN COPIES. The whole feature is this one number.**
 *
 * A boosted move plays as if it held one more copy of the card it is committing — a pair of
 * Bleeds lands as a three, and a lone Stun lands at 50% instead of 25%.
 *
 * **COPIES, NOT A MULTIPLIER, AND THAT IS THE DESIGN.** The deck prices everything it prints in
 * copies and nothing else, so stating the round's boost in the same unit means it needs no
 * pricing of its own and cannot contradict a card face: the face prints `×3` and the fight rolls
 * a three. Three things follow that a raw `×N` could not give:
 *
 * 1. **It is defined for BINARY cards.** A copy of a magic card is a quarter of certainty
 *    ({@link EFFECT_BINARY_BASE_CHANCE}); a `×2` on a chance is undefined at the ceiling — a
 *    full stack is already 100% and doubling it buys nothing or breaks the invariant.
 * 2. **It is bounded.** {@link EFFECT_MELD_MAX} caps the stack, so a boost can never take a
 *    meld past the top of the ladder. A `×2` on a full stack is eight times face value, which
 *    is off the end of every number this deck has ever been balanced against.
 * 3. **It is flat where the ladder is flat.** +1 copy is +1× face value at every rung, so it
 *    helps the small hand exactly as much as the big one. A multiplier pays the player who was
 *    already winning the round, which is the opposite of what a shared, public boon is for.
 *
 * A hand holding NOTHING of a boosted suit still gets nothing: there is no stack for the copy
 * to join. The boost sizes a bet you made; it does not make one for you.
 */
export const ROUND_BOOST_COPIES = 1;

/** Discard-and-redraw opportunities per round (any number of cards, once). */
export const EFFECT_DISCARDS_PER_ROUND = 1;

/**
 * Largest meld that can exist. Not a design choice — only {@link EFFECT_CARD_COPIES}
 * copies of any card are printed, and a meld is copies of ONE card, so five cards in hand
 * can never become a five-card meld.
 */
export const EFFECT_MELD_MAX = EFFECT_CARD_COPIES;

/**
 * The top rung of the copies ladder — every copy the deck prints, played at once.
 *
 * **It is not a kill, and it is not a full heal either.** A full stack multiplies like every
 * other rung, four times face value, and the battle carries on. It has no exceptions left:
 * the kill went when the ladder was flattened, and the healing cards' full-bar version went
 * with it, for the same reason — a card that resolves a fight regardless of HP is not a
 * bigger card, it is a different game.
 */
export const EFFECT_FULL_STACK_COPIES = EFFECT_CARD_COPIES;

/**
 * Copies at which a stack stops being a ring and starts SHOWING OFF.
 *
 * One short of a full stack, and derived from it rather than typed as `3`: a hand that is
 * one card away from the top of the ladder is the loudest thing that can happen during a
 * pitch, and it has to stay one short however many copies the deck prints. Below this a
 * stack is a quiet suit-coloured edge; at this count and above the card glows and sweeps,
 * so the player sees the meld from across the screen instead of reading four corner chips.
 */
export const EFFECT_BLING_COPIES = EFFECT_FULL_STACK_COPIES - 1;

/**
 * **How often a meld on one of the three WHEEL suits actually fires.**
 *
 * An attack, magic or dodge meld is spent whether or not its owner connects, and connecting
 * is roughly a coin flip against an opponent whose gesture is unknown — measured at 47-54%
 * for attack and 63-70% for dodge over a full cards-on roster run (`card-suit-audit.ts`).
 *
 * **DERIVED FROM THE WHEEL, NOT CHOSEN — and it stopped being a tuning knob when it was.**
 *
 * It computes to 0.5, because `getGestureOutcome` says every wheel suit fires on exactly two of
 * the four gestures the opponent can throw: attack 50%, magic 50%, **dodge 50%**. Recover fires
 * on all four, which is why it is the exception described below.
 *
 * **Three attempts were made to move criterion 11 (the recover suit's share of commits) with this
 * constant, and all three are now void.** 0.55 and 0.65 moved it 0.2 and 0.3 points, inside the
 * noise, and were reverted. The third set it to 0.6 and appeared to carry the suit from 14.8% to
 * 15.2%, over the floor — on two premises that both turned out to be false:
 *
 * 1. **The measurement.** Every cards-on simulation in the repo ran with the LEVEL-BASED recover
 *    budget — two or three uses — while every mode that deals cards grants exactly one
 *    (`CARDS_MAX_RECOVER_USES`). With `simulateBattle` corrected the suit reads 10.1%, and this
 *    constant is inert against it: 0.5 → 10.1%, 0.6 → 10.3%, 0.7 → 10.5%.
 * 2. **The justification.** "0.6 is defensible because dodge fires at 66%" — dodge fires at 50%,
 *    like the other two. There was never a range for 0.5 to be the low end of.
 *
 * So it is computed rather than written down, and a future disagreement about it has to be an
 * argument with the wheel. Criterion 11's floor is a separate problem, recorded as one: a single
 * recover across a 5.5-turn battle is a hard ceiling of ~18%, so a 15% floor asks the player to
 * spend it in four battles out of five.
 *
 * **Recover is the exception, and that is the whole reason this constant is here rather than
 * buried in the AI.** Recover sits outside the triangle and beats all three, so a recover
 * meld fires over 90% of the time — it only ever misses to a stun. A card that always
 * connects and a card that connects half the time cannot be printed at the same face value
 * and mean the same thing, so this is the discount the recover suit's own numbers carry (see
 * `getDerivedRecoverHeal`) and the discount the AI charges the other three (`scoreGesture`).
 *
 * **ONE THING THIS CONSTANT NO LONGER MOVES: the Heal card.** Its face was `median(roster
 * recover) * this`, and Row 53 pinned it to the authored `HEAL_CARD_VALUE` so that tuning the
 * recover LADDER could not silently re-price three cards. 11 is still this rule's own output —
 * half the median of 22 the ladder carried when the card was priced — but changing the odds here
 * will not move it now. Move `HEAL_CARD_VALUE` too, on purpose, or the two drift apart.
 *
 * It lives here, with the rest of the deck's pricing, because it is a fact about the DECK —
 * the AI merely reads it, and having it defined in the AI is how the printed cards and the
 * opponent's opinion of them would drift apart.
 */
export const EFFECT_WHEEL_FIRE_ODDS =
  ALL_COMBAT_ACTIONS.filter((theirs) => getGestureOutcome('attack', theirs) !== 'lose').length /
  ALL_COMBAT_ACTIONS.length;

/**
 * The draw pile is refilled from the discard pile when it runs dry.
 *
 * Not optional at this pile size: two players holding five each and burning up to five a
 * round between them will exhaust 64 cards inside a long battle.
 */
export const EFFECT_DECK_RESHUFFLES_WHEN_EMPTY = true;

/**
 * Base chance on a card whose effect has no magnitude — you are stunned or you are not.
 *
 * **One quarter per copy, so the whole stack is exactly certainty.** Every copy printed is
 * a quarter of the effect, four of them are 100%, and nothing on a binary card can be
 * bought past that — which is what makes "collect all four" the ceiling rather than a
 * bonus round. Below the full stack it is a real gamble: a singleton lands one turn in
 * four.
 *
 * Deliberately not the engine's current figures (`forceActionChance` and
 * `blockActionChance` are 0.95, species stun chances run 0.65-1.0): anything that high
 * starts at the cap, so extra copies would buy nothing.
 */
export const EFFECT_BINARY_BASE_CHANCE = 1 / EFFECT_CARD_COPIES;

/**
 * The Common printing's per-copy chance, derived from where its full stack lands rather than
 * authored — so the sentence on the card ("four of these is 75%") is the sentence the engine
 * rolls, and re-pricing the ceiling cannot leave the per-copy rate behind.
 */
export const COMMON_BINARY_BASE_CHANCE = COMMON_BINARY_FULL_STACK_CHANCE / EFFECT_CARD_COPIES;

/**
 * Where a full stack of STUN lands — below certainty, unlike every other binary card.
 *
 * **Stun takes the round AND the opponent's whole turn; a Force only narrows it to one move.**
 * Priced at the same chance, the stun was simply the better card, so it was the one everybody
 * held. A full Rare stack is therefore 75%, and the per-copy rate derives from that rather than
 * being authored, exactly as {@link COMMON_BINARY_BASE_CHANCE} does.
 */
export const STUN_FULL_STACK_CHANCE = 0.75;

/** Stun's per-copy chance at Rare/Epic: its full-stack ceiling split across the copies. */
export const STUN_BINARY_BASE_CHANCE = STUN_FULL_STACK_CHANCE / EFFECT_CARD_COPIES;

/** Stun's Common per-copy chance: the Common share applied on top of the Stun ceiling. */
export const STUN_COMMON_BINARY_BASE_CHANCE = STUN_BINARY_BASE_CHANCE * (COMMON_BINARY_BASE_CHANCE / EFFECT_BINARY_BASE_CHANCE);

/**
 * Every tier's per-copy chance for a BINARY card, in one table.
 *
 * **THE LADDER IS EVEN HERE TOO — the Epic is as far above the Rare as the Common is below it.** It used
 * to stop at Rare ("four Rares is certainty and there is nothing above certain"), which left
 * four copies of Stun, Wound, Block Recover and Force Attack buying NOTHING: the shelf had to
 * print "As strong as this skill gets" on a purple Epic badge. The ceiling was never a wall —
 * `rollBinaryCard` already reads the surplus past 100% as a chance of an EXTRA TURN ("copies buy
 * turns in halves"), and the card face already prints it. So an Epic's reward is where it
 * always was for a magnitude card: a bigger number. Below a full stack it lands sooner (two
 * Epic Wounds are 63%, two Rares 50%); at a full stack a plain card is certain plus a 25%
 * chance of one more turn, and Stun climbs from 75% to 94%.
 *
 * The Common rung stays where it was (a share of its own Rare), so the three steps are
 * Common < Rare < Epic on every card. `cardCatalog.test.ts` pins the order.
 */
export const CARD_TIER_BINARY_CHANCE: Record<CardTier, number> = {
  common: COMMON_BINARY_BASE_CHANCE,
  rare: EFFECT_BINARY_BASE_CHANCE,
  epic: 2 * EFFECT_BINARY_BASE_CHANCE - COMMON_BINARY_BASE_CHANCE,
};

/** {@link CARD_TIER_BINARY_CHANCE} for the Stun card, whose Rare stack tops out at {@link STUN_FULL_STACK_CHANCE}. */
export const STUN_TIER_BINARY_CHANCE: Record<CardTier, number> = {
  common: STUN_COMMON_BINARY_BASE_CHANCE,
  rare: STUN_BINARY_BASE_CHANCE,
  epic: 2 * STUN_BINARY_BASE_CHANCE - STUN_COMMON_BINARY_BASE_CHANCE,
};

/**
 * **WHAT A FULL STACK PAYS ON ITS FACE: twice its copies — ×8 for four.**
 *
 * The rungs below it stay flat (one card is one card, a pair is two), and the top one is a
 * payoff rather than one more step. It was ×4, and the copy ladder in BARS showed what that
 * bought (criterion 19, median share of the other bar when the commit landed):
 *
 * | cards | 1 | 2 | 3 | 4 |
 * |---|---|---|---|---|
 * | of a bar | 22.2% | 21.8% | 22.9% | 24.1% |
 *
 * — four cards of one suit hit no harder than one. The reason is the deck, not the ladder: almost
 * every magnitude card in the three damage suits is a CONDITIONAL rider (Execute, Punish, the
 * two Strikes) or a bleed, so the face value only reaches the bar on the rounds its condition
 * holds, and a flat ×4 on a rider that fires one round in six is nothing a player can feel. The
 * skill under the meld is most of every hit. Doubling the top rung makes the rare round a full
 * stack's condition DOES hold into the round that decides the fight, and it is what moved the two
 * skill criteria: on its own, a good player over a coin flip +22.1 → +25.6pts (`16`) and the move
 * you pick +7.8 → +10.0pts (`17`); with the rest of Row 79, +26.0 and +10.7. It did NOT move the
 * ladder's own step (`19`) — the full commit stayed at 24.1% of a bar at ×5, ×6 and ×8 — which is
 * what {@link FULL_COMMIT_BLOW_MULTIPLIER} is for.
 *
 * It does not touch the binary cards: copies buy CHANCE there (`rollBinaryCard` reads the copy
 * count, not this table), so a full Stun is exactly what it was.
 */
export const EFFECT_FULL_STACK_MULTIPLIER = 2 * EFFECT_FULL_STACK_COPIES;

/**
 * Multiplier a meld applies, by how many copies of the same card it contains.
 *
 * **Flat below the top: N copies is ×N, and a full stack is {@link EFFECT_FULL_STACK_MULTIPLIER}.**
 * No CARD is a special case — a full stack is a bigger number and the battle goes on, because a
 * card that ends a fight regardless of HP is not a bigger card, it is a different game. That is
 * true of everything you spend on the opponent and of the healing cards too, which used to read
 * their top rung as a full HP bar.
 */
export const EFFECT_COPY_MULTIPLIERS: readonly CopyLadderRow[] = Array.from(
  { length: EFFECT_FULL_STACK_COPIES },
  (_, index) => ({
    copies: index + 1,
    multiplier: index + 1 === EFFECT_FULL_STACK_COPIES ? EFFECT_FULL_STACK_MULTIPLIER : index + 1,
  })
);

/**
 * **A FULL COMMIT LANDS ITS WHOLE MOVE 15% HARDER** — the skill and the meld together, after round
 * pressure and before the crit roll and the level gap (`resolveTurn`, through
 * {@link applyFullCommitBlow} in `cardRules.ts`, which the move button reads too).
 *
 * It is the automatic full-commit crit (×1.5) come back at a size the ladder can carry, and fixed
 * rather than rolled. The crit was cut because it paid a third time for the same rarity and made
 * the top rung a CLIFF — 1.71× the rung below against a 1.10-1.50× band (`19`). Without it the
 * fourth card was worth 1.05× the third: a rung deleted rather than tuned, and at the top of the
 * level curve 1.03× (`20`). At 1.15 the step reads 1.19× and 1.20× — a payoff you can see on the
 * bar, nowhere near the cliff.
 *
 * Damage only, and never the recover suit: a full recover commit already lands a DESTINATION
 * (`isFullRecoverCommit` — full bar, conditions cleared), and Reaper's hit rides that commit.
 */
export const FULL_COMMIT_BLOW_MULTIPLIER = 1.15;

/**
 * **THE PARTIAL RECOVER MELD HEALS FOR MORE THAN ITS COPIES.** The one rung-dependent rider
 * in the deck, and the only place the flat ladder above is not the whole story.
 *
 * The ladder is flat because a card that ends a fight regardless of HP is a different game —
 * that reasoning is about DAMAGE, and it left the recover suit with a hole in the middle.
 * Recover is not a rotation option like the other three: it is a BUDGET, capped at
 * `CARDS_MAX_RECOVER_USES` per fighter per battle, so its ceiling is that cap divided by the
 * average turn count and NOT a quarter of the commits. A player cannot answer a weak recover
 * by committing recover more often; the only lever is what one commit is worth. Measured at
 * 11.4% of commits against criterion 11's 15% floor, with a full stack — the rung that buys a
 * clean slate — sitting one rung above a 2-meld worth exactly two cards.
 *
 * So the two middle rungs pay a bonus ON TOP of the flat ladder: a 2-meld heals DOUBLE, a
 * 3-meld two and a half times. Effective healing by copies is therefore ×1, ×4, ×7.5, ×4.
 *
 * **They were ×1.5 and ×2 (a ×3 and a ×6 ladder), and the rule that raised them is the deck
 * thinning** (`sweepDeadCards`): the recover suit now LEAVES the pile the moment its one commit
 * is spent, so the size of that single commit is the entire suit. A rung worth two cards is not
 * a decision when the alternative is keeping the option open for a bigger stack you can still
 * draw into — and the four-stack it approaches is a full bar. The middle of that ladder had to
 * be worth stopping at. Measured: matchup 15/16 and run 11/14 both held, recover's share of
 * commits 17.2% -> 17.7%, comebacks 39.8% inside a 45% ceiling (tuning log Row 42).
 *
 * **A rung not named here pays no bonus**, which is what keeps 1 honest (one card is one
 * card) and leaves the full stack to `isFullRecoverCommit`, whose payoff is a STATE — full
 * bar, no damage, every condition cleared — and not a number this table could size.
 *
 * **It applies to HEALING CARDS, never to the recover SUIT** (`HEALING_CARD_EFFECTS`): the
 * suit also holds the binary Force Recover, which has no magnitude to multiply, and the base
 * recover card, which deals `BASE_CARD_CHIP` rather than healing. Keying this on the suit
 * would quietly buff an empty slot's chip damage.
 */
export const RECOVER_MELD_HEAL_BONUS: Readonly<Record<number, number>> = {
  2: 2,
  3: 2.5,
};

/** Binomial coefficient. Exact at these sizes — the pile never leaves integer range. */
function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let result = 1;
  for (let i = 0; i < k; i += 1) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.round(result);
}

/**
 * How many opening hands have each best-copy-count as their ceiling.
 *
 * COUNTED, not written down, because {@link EFFECT_DECK_TYPES} is derived from the combat
 * wheel and a hardcoded table would start lying the day a gesture moved. The count walks the pile one
 * card type at a time — each type contributes 0..{@link EFFECT_CARD_COPIES} of its copies,
 * in C(copies, k) ways — and files every completed hand under its LARGEST stack, because
 * that is all the multiplier ever reads.
 *
 * Rows can legitimately be zero: with fewer card types than {@link EFFECT_HAND_SIZE}, a hand
 * of all-different cards cannot be dealt at all, so a pair is guaranteed. `cardCatalog.test.ts`
 * re-derives the full sixteen-card case from closed-form binomials and asserts the rows sum
 * to exactly C(pile, hand), so a bug here cannot survive either deck size.
 */
function countHandsByBestStack(): Record<number, number> {
  // ways[cardsUsed][biggestStack] — hands built out of the types walked so far.
  let ways: number[][] = Array.from({ length: EFFECT_HAND_SIZE + 1 }, () =>
    new Array<number>(EFFECT_CARD_COPIES + 1).fill(0)
  );
  ways[0][0] = 1;

  for (let type = 0; type < EFFECT_DECK_TYPES; type += 1) {
    const next = Array.from({ length: EFFECT_HAND_SIZE + 1 }, () =>
      new Array<number>(EFFECT_CARD_COPIES + 1).fill(0)
    );

    for (let used = 0; used <= EFFECT_HAND_SIZE; used += 1) {
      for (let best = 0; best <= EFFECT_CARD_COPIES; best += 1) {
        const count = ways[used][best];
        if (count === 0) continue;

        for (let taken = 0; taken <= Math.min(EFFECT_CARD_COPIES, EFFECT_HAND_SIZE - used); taken += 1) {
          next[used + taken][Math.max(best, taken)] += count * choose(EFFECT_CARD_COPIES, taken);
        }
      }
    }

    ways = next;
  }

  /*
   * **AND THE SUIKAMON CARDS, WHICH TAKE SLOTS WITHOUT EVER MAKING A STACK.**
   *
   * They are in the same pile and are dealt by the same draw, so a hand of five is five cards
   * out of {@link EFFECT_DECK_TOTAL} rather than out of the skills alone — and every one of
   * them that lands is a slot the hand did NOT fill with a copy of something. Folding them in
   * as a final "type" whose `taken` never touches `best` is what makes these rows the odds of
   * the deck the game actually deals, rather than of the deck it dealt before the card existed.
   *
   * **A HAND HOLDS AT MOST ONE SET OF THEM, so `taken` stops at
   * {@link EVOLUTION_CARDS_PER_STEP}.** That is not an approximation of the deal, it IS the deal:
   * a Suikamon card dealt into a hand already holding a whole set is buried and the next card
   * dealt instead (`takeOneForHand` in `cardEngine.ts`), because a fourth can convert nothing
   * this round and a fifth left a fighter with no move card at all. So the hands with four or
   * five of them do not exist to be counted, and a table that counted them would be quoting odds
   * for a deck the game does not deal.
   *
   * **`best` can therefore never come out at 0** — three slots is the most the Suikamon cards can
   * take, so two always hold skills — and the row is kept at zero rather than dropped, so the
   * shape of the table survives a change to either number.
   */
  const withSuikamon = Array.from({ length: EFFECT_HAND_SIZE + 1 }, () =>
    new Array<number>(EFFECT_CARD_COPIES + 1).fill(0)
  );
  for (let used = 0; used <= EFFECT_HAND_SIZE; used += 1) {
    for (let best = 0; best <= EFFECT_CARD_COPIES; best += 1) {
      const count = ways[used][best];
      if (count === 0) continue;
      const room = Math.min(
        SUIKAMON_CARDS_PER_PILE,
        EVOLUTION_CARDS_PER_STEP,
        EFFECT_HAND_SIZE - used
      );
      for (let taken = 0; taken <= room; taken += 1) {
        withSuikamon[used + taken][best] += count * choose(SUIKAMON_CARDS_PER_PILE, taken);
      }
    }
  }
  ways = withSuikamon;

  const counts: Record<number, number> = {};
  // From 0: a stackless hand is a row of the same table, at zero now that the hand can never be
  // all Suikamon cards.
  for (let best = 0; best <= EFFECT_CARD_COPIES; best += 1) {
    counts[best] = ways[EFFECT_HAND_SIZE][best];
  }
  return counts;
}

/** How many opening hands land on each copy count, by their biggest stack. */
export const EFFECT_COPY_HAND_COUNTS: Record<number, number> = countHandsByBestStack();

/**
 * **Every hand the deck can DEAL** — five cards out of the whole pile, minus the ones the
 * Suikamon cap rules out.
 *
 * Summed from {@link EFFECT_COPY_HAND_COUNTS} rather than taken as `choose(pile, 5)`, because
 * those two stopped being the same number when a hand was capped at one set: `choose` counts the
 * hands holding four and five Suikamon cards, and the deck never deals one. Using it as the
 * denominator would have quietly reported every copy row as rarer than it is.
 */
export const EFFECT_TOTAL_HANDS = Object.values(EFFECT_COPY_HAND_COUNTS).reduce(
  (total, hands) => total + hands,
  0
);

/**
 * Chance of being dealt a hand whose best stack is exactly this many copies. Derived from
 * {@link EFFECT_COPY_HAND_COUNTS} rather than duplicated as a second table, so the two can
 * never disagree.
 */
export function getCopyOddsPercent(copies: number): number {
  return ((EFFECT_COPY_HAND_COUNTS[copies] ?? 0) / EFFECT_TOTAL_HANDS) * 100;
}

/** The two shared card assets: one printed frame and one back for every card face. */
export const EFFECT_CARD_FRAME_ART = '/cards/card-frame.png';
export const EFFECT_CARD_BACK_ART = '/cards/card-back.png';
